import { useCallback, useEffect, useState, useRef } from 'react'
import s from './styles.module.scss'
import * as api from 'api'
import { Alert, Layouts, Loader, NoData, Input, Filter } from 'components'
import { head, getTimeFormats } from 'helpers'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import InfiniteScroll from 'components/infiniteScroll'

export default function CallManagement() {
  const navigate = useNavigate()
  const location = useLocation()
  const [loading, setLoading] = useState(false)
  const [calls, setCalls] = useState([])
  const [pageNo, setPageNo] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [filters, setFilters] = useState({
    status: '',
    direction: '',
    outcome: '',
    filter: ''
  })
  const [showCallModal, setShowCallModal] = useState(false)
  const [selectedCall, setSelectedCall] = useState(null)
  const [callForm, setCallForm] = useState({
    userId: '',
    phoneNumber: '',
    contactName: '',
    direction: 'outbound',
    purpose: 'general',
    subject: '',
    notes: ''
  })

  const getCalls = useCallback(async (page = 1) => {
    if (loading) return
    setLoading(true)

    try {
      const response = await api.admin.call.fetchAll({
        pageNo: page,
        perPage: 20,
        ...filters
      })

      if (response.code === 200) {
        if (page === 1) {
          setCalls(response.payload.calls || [])
        } else {
          setCalls(prev => [...prev, ...(response.payload.calls || [])])
        }
        setTotalPages(response.payload.totalPages || 1)
        setPageNo(page)
      } else {
        Alert.error(response.message || 'Failed to fetch calls')
      }
    } catch (error) {
      console.error('Error fetching calls:', error)
      Alert.error('Failed to load calls')
    } finally {
      setLoading(false)
    }
  }, [filters, loading])

  useEffect(() => {
    head({ title: 'Call Management | BCN CRM' })
    getCalls(1)
    
    // Pre-fill form if coming from contacts page
    if (location.state?.userId || location.state?.phoneNumber) {
      setCallForm(prev => ({
        ...prev,
        userId: location.state.userId || '',
        phoneNumber: location.state.phoneNumber || '',
        contactName: location.state.contactName || ''
      }))
      // Auto-open call modal if data is provided
      setShowCallModal(true)
    }
  }, [filters, location.state])

  const handleInitiateCall = async () => {
    if (!callForm.userId && !callForm.phoneNumber) {
      Alert.error('Please provide user ID or phone number')
      return
    }

    setLoading(true)
    try {
      const response = await api.admin.call.create(callForm)
      if (response.code === 201) {
        Alert.success('Call initiated successfully')
        setShowCallModal(false)
        setCallForm({
          userId: '',
          phoneNumber: '',
          contactName: '',
          direction: 'outbound',
          purpose: 'general',
          subject: '',
          notes: ''
        })
        getCalls(1)
        // TODO: Integrate with telephony API (Twilio, etc.) for actual calling
        // For now, this creates a call record
      } else {
        Alert.error(response.message || 'Failed to initiate call')
      }
    } catch (error) {
      console.error('Error initiating call:', error)
      Alert.error('Failed to initiate call')
    } finally {
      setLoading(false)
    }
  }

  const handleUpdateCall = async (callId, updates) => {
    try {
      const response = await api.admin.call.update({
        id: callId,
        ...updates
      })
      if (response.code === 201) {
        Alert.success('Call updated successfully')
        getCalls(1)
      } else {
        Alert.error(response.message || 'Failed to update call')
      }
    } catch (error) {
      console.error('Error updating call:', error)
      Alert.error('Failed to update call')
    }
  }

  const handleCompleteCall = async (call) => {
    await handleUpdateCall(call._id, {
      status: 'completed',
      endTime: new Date()
    })
  }

  return (
    <div className={s.main}>
      <Layouts.Classic title='Call Management'>
        <div className={s.headerActions}>
          <button
            className={s.backButton}
            onClick={() => navigate('/admin/crm')}
          >
            <span className='material-icons-outlined'>arrow_back</span>
            Back to CRM
          </button>
          <button
            className={s.initiateCallBtn}
            onClick={() => setShowCallModal(true)}
          >
            <span className='material-icons-outlined'>phone</span>
            Initiate Call
          </button>
        </div>
      </Layouts.Classic>

      {/* Filters */}
      <div className={s.filters}>
        <Input.Classic
          placeholder='Search by phone, name, or notes...'
          value={filters.filter}
          onChange={e => setFilters(prev => ({ ...prev, filter: e.target.value }))}
          iconLeft='search'
        />
        <Filter
          title='Status'
          heading={filters.status || 'All Statuses'}
          filterFields={[
            { field: '' },
            { field: 'initiated' },
            { field: 'answered' },
            { field: 'completed' },
            { field: 'missed' },
            { field: 'busy' },
            { field: 'failed' }
          ]}
          filterHandler={field => setFilters(prev => ({ ...prev, status: field }))}
        />
        <Filter
          title='Direction'
          heading={filters.direction || 'All Directions'}
          filterFields={[
            { field: '' },
            { field: 'inbound' },
            { field: 'outbound' }
          ]}
          filterHandler={field => setFilters(prev => ({ ...prev, direction: field }))}
        />
        <Filter
          title='Outcome'
          heading={filters.outcome || 'All Outcomes'}
          filterFields={[
            { field: '' },
            { field: 'successful' },
            { field: 'no_answer' },
            { field: 'follow_up_required' },
            { field: 'resolved' },
            { field: 'pending' }
          ]}
          filterHandler={field => setFilters(prev => ({ ...prev, outcome: field }))}
        />
      </div>

      {/* Calls List */}
      <div className={s.content}>
        <InfiniteScroll
          next={getCalls}
          filter=''
          currentPage={pageNo}
          hasMore={pageNo < totalPages}
          inLayout
        >
          {calls.length === 0 && !loading ? (
            <NoData message='No calls found' />
          ) : (
            <div className={s.callsList}>
              {calls.map(call => (
                <CallCard
                  key={call._id}
                  call={call}
                  onUpdate={handleUpdateCall}
                  onComplete={handleCompleteCall}
                  onSelect={setSelectedCall}
                />
              ))}
            </div>
          )}
        </InfiniteScroll>
        {loading && <Loader color='var(--c-primary)' colorText='var(--c-primary)' />}
      </div>

      {/* Call Modal */}
      {showCallModal && (
        <CallModal
          form={callForm}
          setForm={setCallForm}
          onClose={() => setShowCallModal(false)}
          onSubmit={handleInitiateCall}
          loading={loading}
        />
      )}

      {/* Call Details Modal */}
      {selectedCall && (
        <CallDetailsModal
          call={selectedCall}
          onClose={() => setSelectedCall(null)}
          onUpdate={handleUpdateCall}
        />
      )}
    </div>
  )
}

const CallCard = ({ call, onUpdate, onComplete, onSelect }) => {
  const formatDuration = (seconds) => {
    if (!seconds) return '—'
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const getStatusBadge = (status) => {
    const badges = {
      completed: { color: 'green', icon: 'check_circle' },
      answered: { color: 'blue', icon: 'phone' },
      missed: { color: 'red', icon: 'phone_missed' },
      busy: { color: 'orange', icon: 'phone_busy' },
      failed: { color: 'red', icon: 'error' },
      initiated: { color: 'gray', icon: 'phone' }
    }
    return badges[status] || badges.initiated
  }

  const badge = getStatusBadge(call.status)

  return (
    <div className={s.callCard} onClick={() => onSelect(call)}>
      <div className={s.callCardHeader}>
        <div className={s.callCardInfo}>
          <div className={s.callCardName}>
            {call.contactName || call.userId?.firstName || 'Unknown Contact'}
          </div>
          <div className={s.callCardPhone}>{call.phoneNumber}</div>
          {call.userId && (
            <div className={s.callCardUser}>
              User: {call.userId.firstName} {call.userId.lastName} ({call.userId.userRefId})
            </div>
          )}
        </div>
        <div className={`${s.statusBadge} ${s[`badge-${badge.color}`]}`}>
          <span className='material-icons-outlined'>{badge.icon}</span>
          <span>{call.status}</span>
        </div>
      </div>
      <div className={s.callCardDetails}>
        <div className={s.callCardDetail}>
          <span className='material-icons-outlined'>call</span>
          <span>{call.direction === 'inbound' ? 'Inbound' : 'Outbound'}</span>
        </div>
        {call.duration > 0 && (
          <div className={s.callCardDetail}>
            <span className='material-icons-outlined'>schedule</span>
            <span>{formatDuration(call.duration)}</span>
          </div>
        )}
        <div className={s.callCardDetail}>
          <span className='material-icons-outlined'>event</span>
          <span>{call.startTime ? getTimeFormats(new Date(call.startTime)) : '—'}</span>
        </div>
        {call.purpose && (
          <div className={s.callCardDetail}>
            <span className='material-icons-outlined'>label</span>
            <span>{call.purpose}</span>
          </div>
        )}
      </div>
      {call.notes && (
        <div className={s.callCardNotes}>{call.notes}</div>
      )}
      {call.followUpRequired && (
        <div className={s.followUpBadge}>
          <span className='material-icons-outlined'>schedule</span>
          Follow-up required
        </div>
      )}
      {call.status === 'answered' && (
        <button
          className={s.completeBtn}
          onClick={(e) => {
            e.stopPropagation()
            onComplete(call)
          }}
        >
          Complete Call
        </button>
      )}
    </div>
  )
}

const CallModal = ({ form, setForm, onClose, onSubmit, loading }) => {
  return (
    <div className={s.modalOverlay} onClick={onClose}>
      <div className={s.modal} onClick={e => e.stopPropagation()}>
        <div className={s.modalHeader}>
          <h2>Initiate Call</h2>
          <button className={s.closeBtn} onClick={onClose}>
            <span className='material-icons-outlined'>close</span>
          </button>
        </div>
        <div className={s.modalBody}>
          <Input.Classic
            label='User ID (optional)'
            placeholder='Enter user ID'
            value={form.userId}
            onChange={e => setForm(prev => ({ ...prev, userId: e.target.value }))}
          />
          <Input.Classic
            label='Phone Number *'
            placeholder='Enter phone number'
            value={form.phoneNumber}
            onChange={e => setForm(prev => ({ ...prev, phoneNumber: e.target.value }))}
            required
          />
          <Input.Classic
            label='Contact Name'
            placeholder='Enter contact name'
            value={form.contactName}
            onChange={e => setForm(prev => ({ ...prev, contactName: e.target.value }))}
          />
          <Filter
            label='Direction'
            title={form.direction}
            heading={form.direction}
            filterFields={[{ field: 'outbound' }, { field: 'inbound' }]}
            filterHandler={field => setForm(prev => ({ ...prev, direction: field }))}
          />
          <Filter
            label='Purpose'
            title={form.purpose}
            heading={form.purpose}
            filterFields={[
              { field: 'general' },
              { field: 'support' },
              { field: 'sales' },
              { field: 'follow_up' },
              { field: 'verification' },
              { field: 'other' }
            ]}
            filterHandler={field => setForm(prev => ({ ...prev, purpose: field }))}
          />
          <Input.Classic
            label='Subject'
            placeholder='Call subject'
            value={form.subject}
            onChange={e => setForm(prev => ({ ...prev, subject: e.target.value }))}
          />
          <div className={s.modalActions}>
            <button className={s.cancelBtn} onClick={onClose}>Cancel</button>
            <button className={s.submitBtn} onClick={onSubmit} disabled={loading}>
              {loading ? 'Initiating...' : 'Initiate Call'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

const CallDetailsModal = ({ call, onClose, onUpdate }) => {
  const [notes, setNotes] = useState(call.notes || '')
  const [outcome, setOutcome] = useState(call.outcome || 'pending')
  const [followUpRequired, setFollowUpRequired] = useState(call.followUpRequired || false)
  const [followUpNotes, setFollowUpNotes] = useState(call.followUpNotes || '')
  const [saving, setSaving] = useState(false)

  const handleSave = async () => {
    setSaving(true)
    try {
      await onUpdate(call._id, {
        notes,
        outcome,
        followUpRequired,
        followUpNotes
      })
      onClose()
    } catch (error) {
      Alert.error('Failed to update call')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className={s.modalOverlay} onClick={onClose}>
      <div className={s.modal} onClick={e => e.stopPropagation()}>
        <div className={s.modalHeader}>
          <h2>Call Details</h2>
          <button className={s.closeBtn} onClick={onClose}>
            <span className='material-icons-outlined'>close</span>
          </button>
        </div>
        <div className={s.modalBody}>
          <div className={s.callDetailsInfo}>
            <div><strong>Contact:</strong> {call.contactName || 'Unknown'}</div>
            <div><strong>Phone:</strong> {call.phoneNumber}</div>
            <div><strong>Status:</strong> {call.status}</div>
            <div><strong>Direction:</strong> {call.direction}</div>
            {call.duration > 0 && <div><strong>Duration:</strong> {Math.floor(call.duration / 60)}:{(call.duration % 60).toString().padStart(2, '0')}</div>}
            <div><strong>Time:</strong> {call.startTime ? getTimeFormats(new Date(call.startTime)) : '—'}</div>
          </div>
          
          <div className={s.formGroup}>
            <label>Notes</label>
            <textarea
              className={s.textarea}
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder='Add call notes...'
              rows={5}
            />
          </div>

          <div className={s.formGroup}>
            <label>Outcome</label>
            <Filter
              title={outcome}
              heading={outcome}
              filterFields={[
                { field: 'pending' },
                { field: 'successful' },
                { field: 'no_answer' },
                { field: 'busy' },
                { field: 'follow_up_required' },
                { field: 'resolved' },
                { field: 'not_interested' }
              ]}
              filterHandler={setOutcome}
            />
          </div>

          <div className={s.formGroup}>
            <label>
              <input
                type='checkbox'
                checked={followUpRequired}
                onChange={e => setFollowUpRequired(e.target.checked)}
              />
              Follow-up Required
            </label>
          </div>

          {followUpRequired && (
            <div className={s.formGroup}>
              <label>Follow-up Notes</label>
              <textarea
                className={s.textarea}
                value={followUpNotes}
                onChange={e => setFollowUpNotes(e.target.value)}
                placeholder='Add follow-up notes...'
                rows={3}
              />
            </div>
          )}

          <div className={s.modalActions}>
            <button className={s.cancelBtn} onClick={onClose}>Close</button>
            <button className={s.submitBtn} onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

